function _defineProperty(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty(target, key, source[key]);
        });
    }
    return target;
}
function _objectWithoutProperties(source, excluded) {
    if (source == null) return {};
    var target = _objectWithoutPropertiesLoose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _objectWithoutPropertiesLoose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import React, { forwardRef, useContext } from "react";
import memoizeOne from "memoize-one";
import ButtonIcon from "./components/ButtonIcon";
import { disabledTypeEnum, mapDeprecatedAppearanceToUpdated } from "./utils";
import useStyle from "./Button.style";
import { CarinaContext } from "../CarinaProvider/index";
import { injectTestId } from "@cvent/nucleus-test-automation";
import useComposedRef from "../../hooks/useComposedRef";
var isAnchorButtonProps = function(element, props) {
    return element === 'a';
};
var isButtonButtonProps = function(element, props) {
    return !isAnchorButtonProps(element, props);
};
var iconAlignment = function(icon, iconAlign) {
    var alignment = iconAlign === null || iconAlign === void 0 ? void 0 : iconAlign.toLowerCase();
    return {
        iconBefore: icon && alignment === 'start' ? icon : undefined,
        iconAfter: icon && alignment === 'end' ? icon : undefined
    };
};
var Button = forwardRef(function(_param, ref) {
    var accessibilityLabel = _param.accessibilityLabel, accessible = _param.accessible, active = _param.active, tmp = _param.appearance, initialAppearance = tmp === void 0 ? 'lined' : tmp, css = _param.css, disabled = _param.disabled, _element = _param.element, element = _element === void 0 ? 'button' : _element, propRef = _param.forwardedRef, icon = _param.icon, _iconAlign = _param.iconAlign, iconAlign = _iconAlign === void 0 ? 'start' : _iconAlign, iconSize = _param.iconSize, isBlock = _param.isBlock, isElevated = _param.isElevated, isFAB = _param.isFAB, isPill = _param.isPill, isRtl = _param.isRtl, onClick = _param.onClick, _size = _param.size, size = _size === void 0 ? 'm' : _size, testID = _param.testID, text = _param.text, _variant = _param.variant, variant = _variant === void 0 ? 'brand' : _variant, _style = _param.style, propDrop = _objectWithoutProperties(_param, [
        "accessibilityLabel",
        "accessible",
        "active",
        "appearance",
        "css",
        "disabled",
        "element",
        "forwardedRef",
        "icon",
        "iconAlign",
        "iconSize",
        "isBlock",
        "isElevated",
        "isFAB",
        "isPill",
        "isRtl",
        "onClick",
        "size",
        "testID",
        "text",
        "variant",
        "style"
    ]);
    var composedRef = useComposedRef(propRef, ref);
    var mapAppearance = memoizeOne(mapDeprecatedAppearanceToUpdated);
    var _$ref = iconAlignment(icon, iconAlign), iconBefore = _$ref.iconBefore, iconAfter = _$ref.iconAfter;
    var appearance = mapAppearance(initialAppearance);
    var _$ref1;
    var _$ref2 = (_$ref1 = useContext(CarinaContext)) !== null && _$ref1 !== void 0 ? _$ref1 : {
        flags: {
            wrappingButtons: false
        }
    }, wrappingButtons = _$ref2.flags.wrappingButtons;
    var hidden = accessible === undefined ? undefined : !accessible;
    var label = accessibilityLabel !== null && accessibilityLabel !== void 0 ? accessibilityLabel : undefined;
    var _$ref3 = useStyle({
        appearance: appearance,
        variant: variant,
        isPill: isPill,
        isBlock: isBlock,
        iconOnly: icon && !text,
        isRtl: isRtl,
        isElevated: isElevated,
        isFAB: isFAB,
        active: active,
        isDisabled: disabled,
        size: size,
        iconSize: iconSize
    }), buttonStyles = _$ref3.buttonStyles, iconStyles = _$ref3.iconStyles;
    var ButtonContent = _jsxs(_Fragment, {
        children: [
            iconBefore && _jsx(ButtonIcon, {
                icon: iconBefore,
                iconStyle: iconStyles,
                isRtl: isRtl,
                hasChildren: !!text
            }),
            wrappingButtons ? text : text && _jsx("span", {
                css: {
                    minWidth: 0,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis'
                },
                children: text
            }),
            iconAfter && _jsx(ButtonIcon, {
                icon: iconAfter,
                iconStyle: iconStyles,
                isRtl: isRtl,
                hasChildren: !!text,
                reverseSpacing: true
            })
        ]
    });
    var ButtonElement = _jsx(_Fragment, {});
    if (isAnchorButtonProps(element, propDrop)) {
        var href = propDrop.href, rest = _objectWithoutProperties(propDrop, [
            "href"
        ]);
        var _$ref4, _$ref5;
        ButtonElement = _jsx("a", _objectSpread({}, rest, injectTestId(testID), {
            "aria-hidden": (_$ref4 = rest['aria-hidden']) !== null && _$ref4 !== void 0 ? _$ref4 : hidden,
            "aria-label": (_$ref5 = rest['aria-label']) !== null && _$ref5 !== void 0 ? _$ref5 : label,
            css: [
                buttonStyles,
                css
            ],
            ref: composedRef,
            onClick: !disabled ? onClick : undefined,
            href: disabled ? undefined : href,
            "aria-disabled": disabled === disabledTypeEnum.STYLE_ONLY || undefined,
            children: ButtonContent
        }));
    }
    if (isButtonButtonProps(element, propDrop)) {
        var type = propDrop.type, rest1 = _objectWithoutProperties(propDrop, [
            "type"
        ]);
        var _$ref6, _$ref7;
        ButtonElement = _jsx("button", _objectSpread({}, rest1, injectTestId(testID), {
            "aria-hidden": (_$ref6 = rest1['aria-hidden']) !== null && _$ref6 !== void 0 ? _$ref6 : hidden,
            "aria-label": (_$ref7 = rest1['aria-label']) !== null && _$ref7 !== void 0 ? _$ref7 : label,
            css: [
                buttonStyles,
                css
            ],
            ref: composedRef,
            disabled: disabled === true,
            type: type || 'button',
            onClick: !disabled ? onClick : undefined,
            "aria-disabled": disabled === disabledTypeEnum.STYLE_ONLY || undefined,
            children: ButtonContent
        }));
    }
    return ButtonElement;
});
export { Button };
export default Button;
